import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MentionPopover, { useMention } from "@/components/MentionPopover";
import { sanitizeHtml } from "@/lib/sanitize";
import { formatDateIt } from "@/lib/format";
import {
    ArrowLeft, Bold, Italic, Underline, List, ListOrdered, Minus, Link as LinkIcon,
    AtSign, Download, Send, Trash2, Pencil, Check, ChevronDown, ChevronRight, X,
} from "lucide-react";

const TYPES = [{ key: "PA", label: "PA · Politica aziendale" }, { key: "BT", label: "BT · Bollettino tecnico" }];

export default function PolicyEdit() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === "admin";

    const [policy, setPolicy] = useState(null);
    const [seriesList, setSeriesList] = useState([]);
    const [versions, setVersions] = useState([]);
    const [mode, setMode] = useState(null); // "view" | "edit"
    const [err, setErr] = useState("");

    const load = async () => {
        const [{ data }, s, v] = await Promise.all([
            api.get(`/policies/${id}`),
            api.get(`/policies/series`),
            api.get(`/policies/${id}/versions`).catch(() => ({ data: [] })),
        ]);
        setPolicy(data); setSeriesList(s.data); setVersions(v.data || []);
        if (mode == null) setMode(data.status === "draft" && isAdmin ? "edit" : "view");
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    if (!policy) return <div className="p-8 text-sm text-neutral-500">Caricamento…</div>;

    const doDelete = async () => {
        if (!window.confirm("Eliminare definitivamente questa policy?")) return;
        await api.delete(`/policies/${id}`);
        navigate("/policy");
    };

    const doExportPdf = async () => {
        const res = await api.get(`/policies/${id}/pdf`, { responseType: "blob" });
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(policy.title || "policy").replace(/[^a-zA-Z0-9._-]+/g, "_")}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    };

    const isDraft = policy.status === "draft";

    return (
        <div className="p-6 max-w-4xl mx-auto" data-testid="policy-edit-page">
            <div className="flex items-center justify-between mb-4">
                <Link to="/policy" className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900" data-testid="policy-back">
                    <ArrowLeft size={14} /> Torna alle policy
                </Link>
                <div className="flex items-center gap-2">
                    <button onClick={doExportPdf} data-testid="policy-export-pdf"
                        className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-neutral-200 bg-white hover:bg-neutral-50">
                        <Download size={13} /> Esporta PDF
                    </button>
                    {isAdmin && mode === "view" && (
                        <button onClick={() => setMode("edit")} data-testid="policy-edit-btn"
                            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded bg-neutral-900 text-white hover:bg-neutral-800">
                            <Pencil size={13} /> Modifica
                        </button>
                    )}
                    {/* Only drafts get the "Fine modifiche" (auto-saved). Published policies use "Salva nuova versione". */}
                    {isAdmin && mode === "edit" && isDraft && (
                        <button onClick={() => setMode("view")} data-testid="policy-done-btn"
                            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700">
                            <Check size={13} /> Fine modifiche
                        </button>
                    )}
                    {isAdmin && (
                        <button onClick={doDelete} data-testid="policy-delete"
                            className="text-neutral-400 hover:text-red-600 p-1.5" title="Elimina policy">
                            <Trash2 size={14} />
                        </button>
                    )}
                </div>
            </div>

            {err && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="policy-error">{err}</div>}

            {mode === "view" ? (
                <>
                    <PolicyView policy={policy} />
                    <VersionsArchive versions={versions} currentPolicy={policy} />
                </>
            ) : isDraft ? (
                <DraftEditor policy={policy} setPolicy={setPolicy} seriesList={seriesList}
                    onError={setErr} />
            ) : (
                <PublishedEditor policy={policy} seriesList={seriesList}
                    onError={setErr}
                    onCancel={() => setMode("view")}
                    onSaved={async () => { await load(); setMode("view"); }} />
            )}
        </div>
    );
}

/* ============================================================
   READ-ONLY VIEW
   ============================================================ */
function PolicyView({ policy }) {
    const seriesLabel = policy.series && policy.series_number
        ? `SERIE ${String(policy.series).toUpperCase()} N. ${policy.series_number}` : "";

    const onClick = (e) => {
        const el = e.target.closest?.(".policy-mention");
        if (el) {
            e.preventDefault();
            const pid = el.getAttribute("data-policy-id");
            if (pid) window.open(`/policy/${pid}`, "_blank", "noopener");
        }
    };

    return (
        <article className="bg-white border border-neutral-200 rounded-md p-8" data-testid="policy-view">
            <div className="mb-4 flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-neutral-500">
                <span className={`text-[11px] px-1.5 py-0.5 rounded border font-bold ${policy.type === "PA" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-violet-50 text-violet-700 border-violet-200"}`}>
                    {policy.type}
                </span>
                {policy.status === "published" && policy.published_at && (
                    <span data-testid="policy-published-date">Pubblicata il {formatDateIt(policy.published_at)}</span>
                )}
                {policy.status === "draft" && <span className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Bozza</span>}
                {policy.last_modified_at && policy.revision > 0 && (
                    <span data-testid="policy-mod-date">· Modificata {formatDateIt(policy.last_modified_at)} · R{policy.revision}</span>
                )}
            </div>

            <h1 className="font-display text-3xl font-semibold text-neutral-900 tracking-tight" data-testid="policy-title-view">
                {policy.title || <span className="text-neutral-300">(senza titolo)</span>}
            </h1>

            {seriesLabel && (
                <div className="mt-3 inline-block px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium" data-testid="policy-series-label">
                    {seriesLabel}
                </div>
            )}

            {(policy.body_html && policy.body_html.replace(/<[^>]+>/g, "").trim()) ? (
                <div className="mt-6 rich-content policy-body text-neutral-800 leading-relaxed"
                    data-testid="policy-body-view"
                    onClick={onClick}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(policy.body_html) }} />
            ) : (
                <div className="mt-6 text-sm text-neutral-400 italic" data-testid="policy-body-empty">
                    Questa policy non ha ancora contenuto.
                </div>
            )}

            {policy.author_name && (
                <div className="mt-10 pt-4 flex justify-end" data-testid="policy-signature">
                    <div className="text-right text-neutral-500 text-sm leading-tight">
                        <div className="font-medium">{policy.author_name}</div>
                        <div>co-founder</div>
                    </div>
                </div>
            )}
        </article>
    );
}

/* ============================================================
   VERSIONS ARCHIVE (expandable accordion below the policy)
   ============================================================ */
function VersionsArchive({ versions, currentPolicy }) {
    const [expanded, setExpanded] = useState(false);
    const [openId, setOpenId] = useState(null);
    if (!versions || versions.length === 0) return null;

    return (
        <div className="mt-4 bg-neutral-100/60 border border-dashed border-neutral-200 rounded-md overflow-hidden" data-testid="versions-archive">
            <button onClick={() => setExpanded(!expanded)}
                data-testid="versions-archive-toggle"
                className="w-full flex items-center gap-2 px-4 py-2 hover:bg-neutral-100">
                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="font-display text-xs font-medium tracking-wide uppercase text-neutral-500">
                    Versioni precedenti · {versions.length}
                </span>
            </button>
            {expanded && (
                <div className="opacity-90 divide-y divide-neutral-200">
                    {versions.map((v) => {
                        const isOpen = openId === v.id;
                        return (
                            <div key={v.id} className="bg-white" data-testid={`version-${v.revision}`}>
                                <button onClick={() => setOpenId(isOpen ? null : v.id)}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50 text-left">
                                    {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700 border border-neutral-200">
                                        R{v.revision}
                                    </span>
                                    <span className="text-sm text-neutral-800 flex-1 truncate">{v.title}</span>
                                    <span className="text-[11px] text-neutral-500">
                                        {v.snapshotted_at && `archiviata ${formatDateIt(v.snapshotted_at)}`}
                                        {v.snapshotted_by_name && ` · ${v.snapshotted_by_name}`}
                                    </span>
                                </button>
                                {isOpen && (
                                    <div className="px-6 pb-4 pt-1">
                                        {(v.body_html && v.body_html.replace(/<[^>]+>/g, "").trim()) ? (
                                            <div className="rich-content text-neutral-800 text-sm"
                                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(v.body_html) }} />
                                        ) : (
                                            <div className="text-xs text-neutral-400 italic">Contenuto vuoto.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* ============================================================
   DRAFT EDITOR (autosave — for status="draft")
   ============================================================ */
function DraftEditor({ policy, setPolicy, seriesList, onError }) {
    const editorRef = useRef(null);
    const saveTimer = useRef(null);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [showBackdate, setShowBackdate] = useState(false);
    const [backdate, setBackdate] = useState("");
    const mention = useMention(editorRef);

    // Sync editor content when policy changes
    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== (policy.body_html || "")) {
            editorRef.current.innerHTML = sanitizeHtml(policy.body_html || "");
        }
        // eslint-disable-next-line
    }, [policy.id]);

    const scheduleSave = (patch) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            setSaving(true);
            try {
                const { data } = await api.patch(`/policies/${policy.id}`, patch);
                setPolicy(data); setSavedAt(new Date()); onError("");
            } catch (e) { onError(extractError(e)); }
            finally { setSaving(false); }
        }, 600);
    };

    const onEditorInput = () => scheduleSave({ body_html: editorRef.current.innerHTML });
    const onTitle = (e) => { setPolicy({ ...policy, title: e.target.value }); scheduleSave({ title: e.target.value }); };
    const onSeries = (e) => { setPolicy({ ...policy, series: e.target.value }); scheduleSave({ series: e.target.value }); };
    const onType = (e) => { setPolicy({ ...policy, type: e.target.value }); scheduleSave({ type: e.target.value }); };

    const cmd = (name, arg = null) => { editorRef.current.focus(); document.execCommand(name, false, arg); onEditorInput(); };
    const insertLink = () => { const u = window.prompt("URL:"); if (u) cmd("createLink", u); };
    const insertHR = () => cmd("insertHorizontalRule");
    const insertMentionAt = () => {
        editorRef.current.focus();
        document.execCommand("insertText", false, "@");
        setTimeout(() => editorRef.current.dispatchEvent(new Event("keyup", { bubbles: true })), 0);
    };

    const doPublish = async () => {
        try {
            const body = backdate ? { published_at: backdate } : {};
            const { data } = await api.post(`/policies/${policy.id}/publish`, body);
            setPolicy(data); setShowBackdate(false); setBackdate("");
        } catch (e) { onError(extractError(e)); }
    };

    return (
        <>
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded p-3 flex items-center gap-3 text-sm flex-wrap" data-testid="policy-publish-panel">
                <div className="text-amber-800 font-medium">Bozza · auto-salvataggio attivo</div>
                {!showBackdate ? (
                    <button onClick={() => setShowBackdate(true)} data-testid="policy-publish-btn"
                        className="ml-auto inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded bg-amber-600 text-white hover:bg-amber-700">
                        <Send size={13} /> Pubblica
                    </button>
                ) : (
                    <>
                        <div className="text-amber-800">Data pubblicazione:</div>
                        <input type="date" value={backdate} onChange={(e) => setBackdate(e.target.value)}
                            data-testid="policy-backdate-input"
                            className="text-sm border border-amber-300 rounded px-2 py-1" />
                        <span className="text-xs text-amber-700">Vuoto = oggi</span>
                        <button onClick={doPublish} data-testid="policy-confirm-publish"
                            className="ml-auto px-3 py-1 text-sm rounded bg-amber-600 text-white hover:bg-amber-700">
                            Conferma pubblicazione
                        </button>
                    </>
                )}
            </div>

            <EditorShell
                policy={policy}
                titleValue={policy.title || ""} onTitleChange={onTitle}
                seriesValue={policy.series || ""} onSeriesChange={onSeries}
                onType={onType} seriesList={seriesList}
                statusHint={saving ? "Salvataggio…" : savedAt ? `Salvato ${savedAt.toLocaleTimeString()}` : "Auto-salvato"}
                editorRef={editorRef} onEditorInput={onEditorInput}
                mention={mention} cmd={cmd} insertLink={insertLink} insertHR={insertHR} insertMentionAt={insertMentionAt}
            />

            {mention.state && (
                <MentionPopover
                    query={mention.state.query}
                    position={mention.state.position}
                    editorRef={editorRef}
                    onClose={mention.close}
                    onInserted={() => { mention.close(); onEditorInput(); }}
                />
            )}
        </>
    );
}

/* ============================================================
   PUBLISHED EDITOR (buffered — for status="published")
   No autosave; requires explicit "Salva nuova versione" with confirmation.
   ============================================================ */
function PublishedEditor({ policy, seriesList, onError, onCancel, onSaved }) {
    const editorRef = useRef(null);
    const [draft, setDraft] = useState({
        title: policy.title || "",
        body_html: policy.body_html || "",
        series: policy.series || "",
    });
    const [dirty, setDirty] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    const mention = useMention(editorRef);

    // Sync editor content when policy changes
    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== (policy.body_html || "")) {
            editorRef.current.innerHTML = sanitizeHtml(policy.body_html || "");
        }
        // eslint-disable-next-line
    }, [policy.id]);

    // No autosave — every change stays in local `draft` state.
    const markDirty = () => setDirty(true);

    const onEditorInput = () => {
        markDirty();
        setDraft((d) => ({ ...d, body_html: editorRef.current.innerHTML }));
    };
    const onTitle = (e) => { markDirty(); setDraft((d) => ({ ...d, title: e.target.value })); };
    const onSeries = (e) => { markDirty(); setDraft((d) => ({ ...d, series: e.target.value })); };

    const cmd = (name, arg = null) => { editorRef.current.focus(); document.execCommand(name, false, arg); onEditorInput(); };
    const insertLink = () => { const u = window.prompt("URL:"); if (u) cmd("createLink", u); };
    const insertHR = () => cmd("insertHorizontalRule");
    const insertMentionAt = () => {
        editorRef.current.focus();
        document.execCommand("insertText", false, "@");
        setTimeout(() => editorRef.current.dispatchEvent(new Event("keyup", { bubbles: true })), 0);
    };

    const doSaveVersion = async () => {
        setBusy(true);
        try {
            await api.post(`/policies/${policy.id}/new-version`, {
                title: draft.title.trim(),
                body_html: draft.body_html,
                series: draft.series.trim() || null,
            });
            onError("");
            setShowConfirm(false);
            await onSaved();
        } catch (e) { onError(extractError(e)); }
        finally { setBusy(false); }
    };

    const handleCancel = () => {
        if (dirty && !window.confirm("Ci sono modifiche non salvate. Vuoi davvero annullare?")) return;
        onCancel();
    };

    const nextRevision = (policy.revision || 0) + 1;

    return (
        <>
            <div className="mb-4 bg-sky-50 border border-sky-200 rounded p-3 flex items-center gap-3 text-sm flex-wrap" data-testid="policy-version-panel">
                <div className="text-sky-900 font-medium">
                    Stai preparando la versione R{nextRevision}
                </div>
                <div className="text-xs text-sky-700">
                    Le modifiche non vengono salvate automaticamente. Clicca "Salva nuova versione" quando sei pronto.
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <button onClick={handleCancel} data-testid="policy-cancel-version"
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded border border-neutral-300 bg-white hover:bg-neutral-50">
                        <X size={13} /> Annulla
                    </button>
                    <button onClick={() => setShowConfirm(true)}
                        disabled={!dirty || !draft.title.trim()}
                        data-testid="policy-save-version-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        <Check size={13} /> Salva nuova versione
                    </button>
                </div>
            </div>

            <EditorShell
                policy={policy}
                titleValue={draft.title} onTitleChange={onTitle}
                seriesValue={draft.series} onSeriesChange={onSeries}
                onType={null}  // type is not editable on published
                seriesList={seriesList}
                statusHint={dirty ? "Modifiche non salvate" : "Nessuna modifica"}
                editorRef={editorRef} onEditorInput={onEditorInput}
                mention={mention} cmd={cmd} insertLink={insertLink} insertHR={insertHR} insertMentionAt={insertMentionAt}
            />

            {mention.state && (
                <MentionPopover
                    query={mention.state.query}
                    position={mention.state.position}
                    editorRef={editorRef}
                    onClose={mention.close}
                    onInserted={() => { mention.close(); onEditorInput(); }}
                />
            )}

            {showConfirm && (
                <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4"
                    onClick={() => !busy && setShowConfirm(false)} data-testid="save-version-modal">
                    <div onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-md shadow-lg w-full max-w-md">
                        <div className="px-5 py-3 border-b border-neutral-200 font-display font-semibold">
                            Conferma nuova versione
                        </div>
                        <div className="p-5 text-sm text-neutral-700">
                            Stai per salvare le tue modifiche come <b>versione R{nextRevision}</b>.
                            La versione attuale (R{policy.revision || 0}) verrà archiviata e resterà consultabile
                            nella sezione "Versioni precedenti".
                            <div className="mt-3 text-xs text-neutral-500">Vuoi procedere?</div>
                        </div>
                        <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                            <button onClick={() => setShowConfirm(false)} disabled={busy}
                                className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">
                                Annulla
                            </button>
                            <button onClick={doSaveVersion} disabled={busy}
                                data-testid="policy-confirm-save-version"
                                className="px-3 py-1.5 text-sm rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60">
                                {busy ? "Salvataggio…" : `Salva R${nextRevision}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

/* ============================================================
   Shared editor UI shell (meta header, title, series, toolbar, editor, signature)
   ============================================================ */
function EditorShell({
    policy, titleValue, onTitleChange, seriesValue, onSeriesChange, onType, seriesList,
    statusHint, editorRef, onEditorInput, mention, cmd, insertLink, insertHR, insertMentionAt,
}) {
    const seriesLabel = policy.series && policy.series_number
        ? `SERIE ${String(policy.series).toUpperCase()} N. ${policy.series_number}` : "";

    return (
        <div className="bg-white border border-neutral-200 rounded-md p-6">
            <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-neutral-500">
                {onType ? (
                    <select value={policy.type} onChange={onType}
                        data-testid="policy-type-select"
                        className={`text-[11px] px-1.5 py-0.5 rounded border font-bold ${policy.type === "PA" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-violet-50 text-violet-700 border-violet-200"}`}>
                        {TYPES.map((t) => <option key={t.key} value={t.key}>{t.key}</option>)}
                    </select>
                ) : (
                    <span className={`text-[11px] px-1.5 py-0.5 rounded border font-bold ${policy.type === "PA" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-violet-50 text-violet-700 border-violet-200"}`}>
                        {policy.type}
                    </span>
                )}
                {policy.status === "published" && policy.published_at && (
                    <span data-testid="policy-published-date">Pubblicata il {formatDateIt(policy.published_at)}</span>
                )}
                {policy.status === "draft" && <span className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Bozza</span>}
                {policy.last_modified_at && policy.revision > 0 && (
                    <span data-testid="policy-mod-date">· Modificata {formatDateIt(policy.last_modified_at)} · R{policy.revision}</span>
                )}
            </div>

            <input value={titleValue} onChange={onTitleChange}
                data-testid="policy-title"
                placeholder="Titolo policy"
                className="w-full text-3xl font-display font-semibold text-neutral-900 outline-none placeholder:text-neutral-300 border-b border-transparent focus:border-neutral-200 py-1" />

            <div className="mt-2 flex items-center gap-2">
                <input list="policy-series-datalist" value={seriesValue} onChange={onSeriesChange}
                    data-testid="policy-series"
                    placeholder="Argomento / serie (facoltativo)"
                    className="text-sm text-neutral-600 outline-none placeholder:text-neutral-300 border-b border-transparent focus:border-neutral-200 py-0.5" />
                <datalist id="policy-series-datalist">
                    {seriesList.map((s) => <option key={s} value={s} />)}
                </datalist>
            </div>
            {seriesLabel && (
                <div className="mt-3 mb-1 inline-block px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium" data-testid="policy-series-label">
                    {seriesLabel}
                </div>
            )}

            <div className="mt-4 mb-2 flex items-center gap-1 border-b border-neutral-200 pb-2 sticky top-0 bg-white z-10" data-testid="policy-toolbar">
                <ToolBtn onClick={() => cmd("bold")} title="Grassetto" testId="fmt-bold"><Bold size={14} /></ToolBtn>
                <ToolBtn onClick={() => cmd("italic")} title="Corsivo" testId="fmt-italic"><Italic size={14} /></ToolBtn>
                <ToolBtn onClick={() => cmd("underline")} title="Sottolineato" testId="fmt-underline"><Underline size={14} /></ToolBtn>
                <ToolSep />
                <ToolBtn onClick={() => cmd("insertUnorderedList")} title="Elenco puntato" testId="fmt-ul"><List size={14} /></ToolBtn>
                <ToolBtn onClick={() => cmd("insertOrderedList")} title="Elenco numerato" testId="fmt-ol"><ListOrdered size={14} /></ToolBtn>
                <ToolSep />
                <ToolBtn onClick={insertHR} title="Filetto" testId="fmt-hr"><Minus size={14} /></ToolBtn>
                <ToolBtn onClick={insertLink} title="Link" testId="fmt-link"><LinkIcon size={14} /></ToolBtn>
                <ToolBtn onClick={insertMentionAt} title="Menziona policy (@)" testId="fmt-mention"><AtSign size={14} /></ToolBtn>
                <div className="ml-auto text-[11px] text-neutral-400">{statusHint}</div>
            </div>

            <div ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={(e) => { onEditorInput(); mention.handlers.onInput(e); }}
                onKeyUp={mention.handlers.onKeyUp}
                onMouseUp={mention.handlers.onMouseUp}
                onClick={(e) => {
                    const el = e.target.closest?.(".policy-mention");
                    if (el) {
                        e.preventDefault();
                        const pid = el.getAttribute("data-policy-id");
                        if (pid) window.open(`/policy/${pid}`, "_blank", "noopener");
                    }
                }}
                data-testid="policy-editor"
                className="mt-3 min-h-[400px] rich-content plan-editor text-neutral-800 outline-none" />

            {policy.author_name && (
                <div className="mt-8 pt-4 flex justify-end" data-testid="policy-signature">
                    <div className="text-right text-neutral-500 text-sm leading-tight">
                        <div className="font-medium">{policy.author_name}</div>
                        <div>co-founder</div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ToolBtn({ children, onClick, title, testId }) {
    return (
        <button type="button" onClick={onClick} title={title} data-testid={testId}
            className="p-1.5 rounded text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100">
            {children}
        </button>
    );
}
function ToolSep() { return <span className="w-px h-4 bg-neutral-200 mx-1" />; }
